import { apiFetch, apiUploadFile } from '@/lib/api-client';
import type { BuildFileKind, BuildFileStatus, BuildFilesResponse } from '@uniticket/shared';

// The Build reference files (service file / deployment list / blacklist). Since R1 (2026-09-25) the server
// fetches them from SharePoint every morning at 6; reading their status is open to the whole console,
// « Actualiser » and the manual upload are admin-only and enforced server-side.

/** The file loaded for each kind (name, date of the file, load date, source, rows) and the sync's state. */
export async function fetchBuildFiles() {
  return apiFetch<BuildFilesResponse>('/build-files');
}

/** « Actualiser » : the server starts a sync in the background and answers at once (202). */
export async function startBuildFilesSync() {
  return apiFetch<{ data: { started: boolean } }>('/build-files/sync', { method: 'POST' });
}

/**
 * Gzip in the browser before uploading. The real exports are ~47 MB of CSV and compress about 8.5x, so
 * the request goes out at ~5 MB: the server's raw-body cap stays modest and the shared platform nginx
 * needs no widening — which matters, since that nginx serves twenty other stacks. A browser without
 * CompressionStream simply sends the file as-is; the server sniffs the gzip magic bytes either way, so
 * both paths work without a flag.
 */
async function compress(file: File): Promise<Blob> {
  if (typeof CompressionStream === 'undefined') return file;
  return await new Response(file.stream().pipeThrough(new CompressionStream('gzip'))).blob();
}

/** Replace the current file of a kind. Returns the new status (row count included). */
export async function uploadBuildFile(kind: BuildFileKind, file: File) {
  const body = await compress(file);
  return apiUploadFile<{ data: BuildFileStatus }>(
    `/build-files/${kind}?filename=${encodeURIComponent(file.name)}`,
    body,
  );
}
